import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { propsOf, strProp, toMillis } from './_graphHelpers';

const STALE_THRESHOLD_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * P0.3 — stale resource (last-modified older than 90 days).
 * Severity: info. Requires the scan to carry `lastTouched` /
 * `lastModified` / `updatedAt` on each resource. Stays quiet when
 * absent — no false positives on scans that don't track it.
 *
 * P0.1 compare mode eventually replaces this with a proper last-change
 * derivation from snapshot history; today we rely on the scan attr.
 */
export const staleResourceRule: FindingRule = {
  id: 'stale_resource',
  label: 'Stale resource',
  description: 'Resource not modified in the last 90 days.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const now = Date.now();
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      const p = propsOf(node);
      // Modification stamps win over creation stamps — a long-lived
      // resource that was touched yesterday isn't stale. After the
      // infra-capture audit walkers thread `createdAt` everywhere we
      // also accept that as a fallback so the rule stops staying
      // silent on scans that only carry creation time.
      const stamp =
        strProp(p, ['lastTouched']) ??
        strProp(p, ['lastModified']) ??
        strProp(p, ['updatedAt']) ??
        strProp(p, ['createdAt']);
      const t = toMillis(stamp);
      if (!Number.isFinite(t)) continue;
      if (now - t < STALE_THRESHOLD_MS) continue;
      const days = Math.floor((now - t) / (24 * 60 * 60 * 1000));
      findings.push({
        id: `stale_resource:resource:${node.id}`,
        ruleId: 'stale_resource',
        severity: 'info',
        scope: 'resource',
        subject: { kind: 'resource', id: node.id },
        title: 'Stale resource',
        detail: `${String(node.label ?? node.id)} last modified ${days} days ago.`,
        attrs: { days },
      });
    }
    return findings;
  },
};
